import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { UserPlus, Loader2, Copy, Check, Link2, Mail } from "lucide-react";
import { toast } from "sonner";

export const inviteLink = (token) => `${window.location.origin}/invite/${token}`;

export function CreateInviteDialog({ onCreated, trigger }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("reusable");
  const [email, setEmail] = useState("");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  const reset = () => { setType("reusable"); setEmail(""); setCreated(null); setCopied(false); };

  const create = async () => {
    if (type === "single" && !email.trim()) { toast.error("Enter the candidate's email"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/candidate-invites", { type, email: email.trim() });
      setCreated(data);
      onCreated && onCreated();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  const copy = () => {
    navigator.clipboard.writeText(inviteLink(created.token));
    setCopied(true);
    toast.success("Invite link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        {trigger || (
          <Button data-testid="invite-candidate-btn" variant="ghost" className="rounded-none border border-black/10 hover:bg-black/5">
            <UserPlus className="h-4 w-4 mr-1" /> Invite
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="bg-white border-black/10 rounded-none">
        <DialogHeader><DialogTitle className="font-heading">Invite a candidate</DialogTitle></DialogHeader>

        {!created ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Generate a link candidates use to create their own portal account. Their profile is created automatically and tagged as invited by you.</p>
            <div className="grid grid-cols-2 gap-px bg-black/10 border border-black/10">
              <button type="button" data-testid="invite-type-reusable" onClick={() => setType("reusable")}
                className={`p-4 text-left transition-colors ${type === "reusable" ? "bg-[#09090B] text-white" : "bg-white hover:bg-black/5"}`}>
                <span className="flex items-center gap-2 font-heading font-bold text-sm"><Link2 className="h-4 w-4" /> Reusable link</span>
                <span className={`text-xs mt-1 block ${type === "reusable" ? "text-white/60" : "text-muted-foreground"}`}>Share anywhere — anyone can join as a candidate.</span>
              </button>
              <button type="button" data-testid="invite-type-single" onClick={() => setType("single")}
                className={`p-4 text-left transition-colors ${type === "single" ? "bg-[#09090B] text-white" : "bg-white hover:bg-black/5"}`}>
                <span className="flex items-center gap-2 font-heading font-bold text-sm"><Mail className="h-4 w-4" /> One-time link</span>
                <span className={`text-xs mt-1 block ${type === "single" ? "text-white/60" : "text-muted-foreground"}`}>Tied to one email, expires after they join.</span>
              </button>
            </div>
            {type === "single" && (
              <div className="space-y-2">
                <Label>Candidate email</Label>
                <Input data-testid="invite-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" className="bg-[#F4F4F5] border-black/10 rounded-none" />
              </div>
            )}
            <Button onClick={create} disabled={creating} data-testid="invite-generate-btn" className="w-full rounded-none bg-primary hover:bg-[#09090B]">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate invite link"}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {created.type === "single" ? <>One-time invite for <b>{created.email}</b>. Share this link:</> : <>Reusable invite link. Share it anywhere:</>}
            </p>
            <div className="flex items-center gap-2">
              <Input readOnly value={inviteLink(created.token)} data-testid="invite-link-field" className="bg-[#F4F4F5] border-black/10 rounded-none text-xs" onFocus={(e) => e.target.select()} />
              <Button onClick={copy} data-testid="invite-copy-btn" className="rounded-none bg-primary hover:bg-[#09090B] shrink-0">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <Button variant="ghost" onClick={reset} className="w-full rounded-none border border-black/10">Create another</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
